import type { PreviewLocalization } from "@intelligems/ig-clients/storefront-navigation/preview-localization";
import { toStorefrontCountryCode } from "@intelligems/ig-clients/storefront-navigation/country-code";
import { toStorefrontLanguageCode } from "@intelligems/ig-clients/storefront-navigation/language-code";
import { createSelector } from "@reduxjs/toolkit";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";

/** An `ig:shopify` field that the Preview localization needs. */
export type PreviewLocalizationField = "country" | "locale" | "routes.root";

export type PreviewLocalizationResult =
  /** The preview has not sent `ig:shopify` yet. */
  | { status: "unavailable" }
  /**
   * The preview sent `ig:shopify` without a field that the Preview
   * localization needs, for example from a reverse proxy build that does not
   * report `routes.root`. Another message does not add the field.
   */
  | { status: "incomplete"; missing: PreviewLocalizationField[] }
  | { status: "available"; localization: PreviewLocalization }
  | { status: "unknownLocale"; locale: string }
  | { status: "unknownCountry"; country: string };

const UNAVAILABLE: PreviewLocalizationResult = { status: "unavailable" };

/**
 * The Preview localization: the country, language and locale root that
 * Shopify resolved for the page in the Embedded preview, as the preview
 * reports them in `ig:shopify`. The state keeps the reported locale; the
 * conversion to the Storefront `LanguageCode` and the check of the country
 * against the Storefront `CountryCode` happen here. The result is
 * "unavailable" until the first `ig:shopify` message, and "incomplete" when
 * a message does not report all three values.
 */
export const getPreviewLocalization = createSelector(
  experienceBuilderSlice.selectors.getShopifyContext,
  (context): PreviewLocalizationResult => {
    if (!context) {
      return UNAVAILABLE;
    }

    const { country, locale } = context;
    const root = context.routes?.root;
    if (!country || !locale || !root) {
      const missing: PreviewLocalizationField[] = [];
      if (!country) {
        missing.push("country");
      }
      if (!locale) {
        missing.push("locale");
      }
      if (!root) {
        missing.push("routes.root");
      }
      return { status: "incomplete", missing };
    }

    const language = toStorefrontLanguageCode(locale);
    if (!language.ok) {
      return { status: "unknownLocale", locale };
    }

    const storefrontCountry = toStorefrontCountryCode(country);
    if (!storefrontCountry.ok) {
      return { status: "unknownCountry", country };
    }

    return {
      status: "available",
      localization: {
        country: storefrontCountry.country,
        language: language.language,
        root,
      },
    };
  }
);
